import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AccommodationTagState } from "../objects/accommodationTagState";
import { AccommodationTag } from "../objects/accommodationTag";
import { EMPTY_ACCOMMODATION_TAG_LIST_CONFIGURATION, EMPTY_ACCOMMODATION_TAG_LIST_CRITERIA } from "../constants/accommodationTagConstants";
import { AccommodationTagListCriteria } from "../objects/accommodationTagList";
import { Preferences } from "../../profile/objects/preferences";
import { setAccommodationTagPreference } from "../utils/setAccommodationTagPreference";
import { cloneDeep } from "lodash";
import { Column } from "../objects/column";

const initialState: AccommodationTagState = {
    accommodation_tag_list: [],
    accommodation_tag_list_loaded: false,
    accommodation_tag_list_refresh: false,
    accommodation_tag_list_count: 0,
    accommodation_tag_list_criteria: EMPTY_ACCOMMODATION_TAG_LIST_CRITERIA,
    accommodation_tag_list_configuration: EMPTY_ACCOMMODATION_TAG_LIST_CONFIGURATION,
    accommodation_tag_list_loading: false,
    accommodation_tag: null,
    accommodation_tag_delete: null,
    first_preference_loaded: false
};



// colonnes
export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setPreferenceReducer = (state: AccommodationTagState, action: PayloadAction<Preferences[]>): AccommodationTagState => {

    const accommodation_tag_preference = setAccommodationTagPreference(action.payload, state.accommodation_tag_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        accommodation_tag_list_configuration: { ...state.accommodation_tag_list_configuration, preferences: accommodation_tag_preference.preferences, columns: accommodation_tag_preference.columns },
        accommodation_tag_list_criteria: { ...state.accommodation_tag_list_criteria, order: accommodation_tag_preference.order, order_by: accommodation_tag_preference.order_by },
    };
};

const addAccommodationTagListConfigurationPreferenceReducer = (state: AccommodationTagState, action: PayloadAction<Preferences[]>): AccommodationTagState => ({
    ...state, accommodation_tag_list_configuration: { ...state.accommodation_tag_list_configuration, preferences: [...state.accommodation_tag_list_configuration.preferences, ...action.payload] }
});
const setAccommodationTagListConfigurationPreferenceReducer = (state: AccommodationTagState, action: PayloadAction<Preferences[]>): AccommodationTagState => ({
    ...state, accommodation_tag_list_configuration: { ...state.accommodation_tag_list_configuration, preferences: action.payload }
});


const toggleAccommodationTagColumnReducer = (state: AccommodationTagState, action: PayloadAction<number>): AccommodationTagState => {
    const columns = cloneDeep([...state.accommodation_tag_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, accommodation_tag_list_configuration: { ...state.accommodation_tag_list_configuration, columns } };
};
const setAccommodationTagColumnsReducer = (state: AccommodationTagState, action: PayloadAction<Column[]>): AccommodationTagState => ({
    ...state, accommodation_tag_list_configuration: { ...state.accommodation_tag_list_configuration, columns: action.payload }
});

// list
const setAccommodationTagListReducer = (state: AccommodationTagState, action: PayloadAction<AccommodationTag[]>): AccommodationTagState => ({
    ...state, accommodation_tag_list: action.payload, accommodation_tag_list_loaded: true
});
const setAccommodationTagListCountReducer = (state: AccommodationTagState, action: PayloadAction<number>): AccommodationTagState => ({
    ...state, accommodation_tag_list_count: action.payload
});

const setAccommodationTagListLoadingReducer = (state: AccommodationTagState, action: PayloadAction<boolean>): AccommodationTagState => ({
    ...state, accommodation_tag_list_loading: action.payload
});
const setAccommodationTagListCriteriaReducer = (state: AccommodationTagState, action: PayloadAction<AccommodationTagListCriteria>): AccommodationTagState => ({
    ...state, accommodation_tag_list_criteria: action.payload
});
const toggleRefreshAccommodationTagListReducer = (state: AccommodationTagState): AccommodationTagState => ({
    ...state, accommodation_tag_list_refresh: !state.accommodation_tag_list_refresh
});

// CRUD
const setAccommodationTagReducer = (state: AccommodationTagState, action: PayloadAction<AccommodationTag | null>): AccommodationTagState => ({
    ...state, accommodation_tag: action.payload
});
const setAccommodationTagDeleteReducer = (state: AccommodationTagState, action: PayloadAction<AccommodationTag | null>): AccommodationTagState => ({
    ...state, accommodation_tag_delete: action.payload
});

export const accommodationTag = createSlice({
    name: 'accommodation_tag',
    initialState,
    reducers: {
        setAccommodationTagList: setAccommodationTagListReducer,
        setAccommodationTagListCount: setAccommodationTagListCountReducer,
        setAccommodationTagListLoading: setAccommodationTagListLoadingReducer,
        setAccommodationTag: setAccommodationTagReducer,
        setAccommodationTagDelete: setAccommodationTagDeleteReducer,
        setAccommodationTagListCriteria: setAccommodationTagListCriteriaReducer,
        toggleRefreshAccommodationTagList: toggleRefreshAccommodationTagListReducer,
        addAccommodationTagListConfigurationPreference: addAccommodationTagListConfigurationPreferenceReducer,
        setAccommodationTagListConfigurationPreference: setAccommodationTagListConfigurationPreferenceReducer,
        toggleAccommodationTagColumn: toggleAccommodationTagColumnReducer,
        setAccommodationTagColumns: setAccommodationTagColumnsReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setAccommodationTagList,
    setAccommodationTagListCount,
    setAccommodationTagListLoading,
    setAccommodationTag,
    setAccommodationTagDelete,
    setAccommodationTagListCriteria,
    toggleRefreshAccommodationTagList,
    addAccommodationTagListConfigurationPreference,
    setAccommodationTagListConfigurationPreference,
    toggleAccommodationTagColumn,
    setAccommodationTagColumns
} = accommodationTag.actions;